import { Button, Flex, Input, Text, VStack } from '@chakra-ui/react';
import axios from 'axios';
import { ReactElement, useRef, useState } from 'react';
import { activateAdminImpersonationSession } from 'utils/auth';
import { StatusCode } from '@novaheal/types';
import { useFeatureFlagsContext } from 'hooks/utils/useFeatureFlagContext';
import * as Sentry from '@sentry/react';
import { CustomModal } from '../CustomModal';
import { CustomModalContent } from '../CustomModalContent';

type Props = {
  isOpen: boolean;
  adminToken: string;
  onSuccess: (accessToken: string) => void;
  onClose: () => void;
};

/**
 * Modal that prompts the admin to enter the 4-digit PIN associated with an
 * impersonation session. Shows remaining attempts and locks after MAX_ATTEMPTS.
 */
export const AdminPinModal = ({
  isOpen,
  adminToken,
  onSuccess,
  onClose,
}: Props): ReactElement | null => {
  const featureFlags = useFeatureFlagsContext();
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  if (!featureFlags.isEnabled('isAdminImpersonationEnabled')) {
    Sentry.captureMessage(
      'AdminPinModal rendered while admin impersonation feature flag is disabled'
    );
    return null;
  }

  const handleSubmit = async (): Promise<void> => {
    if (isLocked) return;

    setIsLoading(true);
    setError(null);

    try {
      const { accessToken } = await activateAdminImpersonationSession(adminToken, pin);
      onSuccess(accessToken);
    } catch (err: unknown) {
      setPin('');
      inputRef.current?.focus();

      if (axios.isAxiosError(err)) {
        const data = err.response?.data;
        const codeError: string | undefined = data?.codeError;

        if (codeError === StatusCode.ADMIN_SESSION_STATUS_INVALIDATED) {
          setIsLocked(true);
          setError('Zu viele falsche Versuche. Die Session wurde ungültig gemacht.');
        } else if (codeError === StatusCode.ADMIN_PIN_INCORRECT) {
          const remaining: number = data?.remainingAttempts;
          setError(`Falscher PIN. Noch ${remaining} Versuch${remaining === 1 ? '' : 'e'} übrig.`);
        } else {
          setError(data?.message ?? 'Falscher PIN.');
        }
      } else {
        setError('Ein Fehler ist aufgetreten. Bitte erneut versuchen.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePinChange = (value: string): void => {
    setPin(value);
  };

  return (
    <CustomModal isOpen={isOpen} onClose={onClose} placement="center" size="sm" hideHeader>
      <CustomModalContent p="30px">
        <VStack gap="20px" align="stretch">
          <VStack gap="8px" align="flex-start">
            <Text textStyle="h4.medium" color="text.primary">
              Admin-Session PIN
            </Text>
            <Text textStyle="body.s.medium" color="text.secondary">
              Bitte gib den PIN ein.
            </Text>
          </VStack>

          <Input
            ref={inputRef}
            type="text"
            placeholder="PIN eingeben"
            value={pin}
            onChange={(e) => handlePinChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSubmit();
            }}
            disabled={isLocked}
          />

          {error && (
            <Text textStyle="body.s.regular" color="red.500">
              {error}
            </Text>
          )}

          <Flex gap="m" justify="flex-end">
            <Button variant="tertiary" onClick={onClose} disabled={isLoading}>
              Abbrechen
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={isLoading}
              disabled={isLocked}
            >
              Bestätigen
            </Button>
          </Flex>
        </VStack>
      </CustomModalContent>
    </CustomModal>
  );
};
